import { Box, Button, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const enum ACTION_LABELS {
  ACCEPT = 'actions.accept',
  CONTINUE = 'actions.continue',
  PREVIOUS = 'actions.previous',
  REJECT = 'actions.reject',
  CANCEL = 'actions.cancel',
}

interface ConsentTermAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

interface ConsentTermActionsProps {
  showIdentityForm: boolean;
  isLastStep: boolean;
  activeStep: number;
  setActiveStep: (step: number) => void;
  isFormValid?: boolean;
  onFormSubmit?: () => Promise<void>;
  onConsentSubmit?: (consentValue: boolean) => Promise<void>;
  isSubmittingConsent?: boolean;
  consented?: boolean | null;
}

const ConsentTermActions = ({
  showIdentityForm,
  isLastStep,
  activeStep,
  setActiveStep,
  isFormValid = true,
  onFormSubmit,
  onConsentSubmit,
  isSubmittingConsent = false,
  consented: _consented,
}: ConsentTermActionsProps) => {
  const [params] = useSearchParams();
  const key = params.get('key');
  const navigate = useNavigate();
  const { t } = useTranslation();

  function determineActions(): ConsentTermAction[] {
    if (isLastStep) {
      return [
        {
          label: isSubmittingConsent
            ? t('actions.submitting')
            : t(ACTION_LABELS.ACCEPT),
          onClick: async () => {
            if (onConsentSubmit) {
              await onConsentSubmit(true);
            }
          },
          disabled: isSubmittingConsent,
        },
        {
          label: isSubmittingConsent
            ? t('actions.submitting')
            : t(ACTION_LABELS.REJECT),
          onClick: async () => {
            if (onConsentSubmit) {
              await onConsentSubmit(false);
            }
          },
          disabled: isSubmittingConsent,
        },
      ];
    }

    if (showIdentityForm) {
      const actions: ConsentTermAction[] = [];

      actions.push({
        label: t(ACTION_LABELS.CONTINUE),
        onClick: async () => {
          if (activeStep === 1 && onFormSubmit) {
            // Step 1: Submit the form and move to next step
            try {
              await onFormSubmit();
            } catch (error) {
              console.error('Form submission failed:', error);
              // Don't move to next step if submission fails
              return;
            }
          } else {
            // Step 0: Just move to next step
            setActiveStep(activeStep + 1);
          }
        },
        disabled: !isFormValid,
      });

      if (activeStep === 0) {
        actions.push({
          label: t(ACTION_LABELS.CANCEL),
          onClick: () => {
            navigate(`/?key=${key}`);
          },
          disabled: false,
        });
      } else {
        actions.push({
          label: t(ACTION_LABELS.PREVIOUS),
          onClick: () => {
            setActiveStep(activeStep - 1);
          },
        });
      }

      return actions;
    }

    return [
      {
        label: isSubmittingConsent
          ? t('actions.submitting')
          : t(ACTION_LABELS.ACCEPT),
        onClick: async () => {
          if (onConsentSubmit) {
            await onConsentSubmit(true);
          }
        },
        disabled: isSubmittingConsent,
      },
      {
        label: isSubmittingConsent
          ? t('actions.submitting')
          : t(ACTION_LABELS.REJECT),
        onClick: async () => {
          if (onConsentSubmit) {
            await onConsentSubmit(false);
          }
        },
        disabled: isSubmittingConsent,
      },
    ];
  }

  const actions = determineActions();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        paddingTop: 2,
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 0,
        backgroundColor: 'transparent',
        flexShrink: 0,
        height: 'auto',
        boxSizing: 'border-box',
      }}
    >
      <Button
        variant="contained"
        sx={{
          width: '100%',
          padding: '10px 16px',
          color: '#ffffff',
          backgroundColor: '#364B5E !important',
          height: '48px',
          textTransform: 'none',
          borderRadius: '32px',
          '&.Mui-disabled': {
            backgroundColor: '#cccccc !important',
            color: '#666666 !important',
            cursor: 'not-allowed !important',
          },
        }}
        onClick={actions[0].onClick}
        disabled={actions[0].disabled}
      >
        <Typography variant="bodyLarge">{actions[0].label}</Typography>
      </Button>
      <Button
        variant="outlined"
        sx={{
          width: '100%',
          padding: '9px 16px',
          color: '#364B5E',
          borderColor: '#364B5E',
          borderWidth: '1px',
          height: '48px',
          textTransform: 'none',
          borderRadius: '32px',
        }}
        onClick={actions[1].onClick}
        disabled={actions[1].disabled}
      >
        <Typography variant="bodyLarge">{actions[1].label}</Typography>
      </Button>
    </Box>
  );
};

export default ConsentTermActions;
